import axiosbase, {AxiosError} from "axios";
import firebase from "firebase";

const URL = process.env.REACT_APP_API_URL;

interface RequestHeader {
    "Content-Type": string;
    Authorization?: string;
}

export const axiosBase = async (authRequired: boolean) => {
    const reqHeader: RequestHeader = {
        "Content-Type": "application/json",
    };
    if (authRequired) {
        const authToken = await firebase.auth().currentUser?.getIdToken(true);
        reqHeader.Authorization = `Bearer ${authToken}`;
    }
    return axiosbase.create({
        baseURL: URL,
        headers: reqHeader,
        responseType: "json",
    });
};

export interface SNSData {
    type: string;
    title?: string;
    content: string;
    pictureUrl?: string;
    originalUrl: string;
    owner: string;
    ownerIcon: string;
    ownerPage: string;
    meta?: {
        comment: number;
        retweet: number;
        like: number;
    };
    createdAt?: Date;
}

export interface AccountData {
    id: number;
    serviceType: string;
    name: string;
    imageUrl: string;
}

export interface SNSDataList {
    id: number;
    name?: string;
    snsData: SNSData[];
    accounts: AccountData[];
}

export interface ListItem {
    id: number;
    name: string;
}

export interface CreateListResponse {
    id: number;
    name: string;
}

export interface LoginResponse {
    id: number;
    name: string;
    firebaseUid: string;
    createdAt: string;
    updatedAt: string;
}

export interface AddAccountRequest {
    accountId: string;
    serviceType: string;
}

export interface DeleteAccountRequest {
    id: number;
    serviceType: string;
}

export interface AddAccountResponse {
    id: number;
    twitterAccountId?: string;
    youtubeChannelId?: string;
    listId: number;
}

export interface ErrorResponse {
    status: number;
    statusText: string;
}

const makeError = (e: AxiosError): ErrorResponse => {
    if (!e.response) {
        return {status: 500, statusText: "Network Error"};
    }
    return {status: e.response.status, statusText: e.response.statusText};
};

export const isErrorResponse = (e: any): e is ErrorResponse => {
    return e && e.status && e.statusText;
};

export const getLists = async (): Promise<Array<ListItem> | ErrorResponse> => {
    try {
        const res = await (await axiosBase(true)).get<Array<ListItem>>(
            `/lists`,
            {}
        );
        return res.data;
    } catch (e) {
        console.error(e);
        return makeError(e);
    }
};

export const getListsDetail = async (
    listId: number
): Promise<SNSDataList | ErrorResponse> => {
    try {
        const res = await (await axiosBase(true)).get<SNSDataList>(
            `/lists/${listId}`,
            {}
        );
        return res.data;
    } catch (e) {
        console.error(e);
        return makeError(e);
    }
};

export const postList = async (
    listName: string
): Promise<CreateListResponse | ErrorResponse> => {
    const body = {
        name: listName,
    };
    try {
        const res = await (await axiosBase(true)).post<CreateListResponse>(
            `/lists`,
            body,
            {}
        );
        return res.data;
    } catch (e) {
        console.error(e);
        return makeError(e);
    }
};

export const deleteList = async (
    listId: number
): Promise<null | ErrorResponse> => {
    try {
        const res = await (await axiosBase(true)).delete<null>(
            `/lists/${listId}`,
            {}
        );
        return res.data;
    } catch (e) {
        console.error(e);
        return makeError(e);
    }
};

export const postAccount = async (
    listId: number,
    body: AddAccountRequest
): Promise<AddAccountResponse | ErrorResponse> => {
    try {
        const res = await (await axiosBase(true)).post<AddAccountResponse>(
            `/lists/${listId}`,
            body,
            {}
        );
        return res.data;
    } catch (e) {
        console.error(e);
        return makeError(e);
    }
};

export const deleteAccount = async (
    request: DeleteAccountRequest
): Promise<AddAccountResponse | ErrorResponse> => {
    try {
        console.info(request);
        const res = await (await axiosBase(true)).request<AddAccountResponse>({
            method: "delete",
            url: "lists/accounts",
            data: request,
        });
        return res.data;
    } catch (e) {
        console.error(e);
        return makeError(e);
    }
};

export const login = async (authToken: string): Promise<LoginResponse> => {
    const body = {
        firebaseTokenId: authToken,
    };
    try {
        const res = await (await axiosBase(false)).post<LoginResponse>(
            `/login`,
            body
        );
        return res.data;
    } catch (e) {
        console.error(e);
        throw makeError(e);
    }
};
